import { useEffect, useState } from "react";
import { MapPin, Phone, Mail, CheckCircle2, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import ContactSections, { withContactDefaults } from "@/components/public/ContactSections";
import api from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import { usePageContent } from "@/hooks/usePageContent";
import { Icon } from "@/lib/icon";
import { Button } from "@/components/ui/button";
import Header from "@/components/public/Header";
import Footer from "@/components/public/Footer";
import { useLead } from "@/components/public/LeadDialog";
import { Spinner } from "./LocationDetail";

function Shell({ path, children }) {
  useSeo(path);
  const [settings, setSettings] = useState({});
  useEffect(() => { api.get("/public/homepage").then((r) => setSettings(r.data.settings)); }, []);
  return (
    <div className="bg-white min-h-screen">
      <Header phone={settings.phone} />
      {children(settings)}
      <Footer settings={settings} />
    </div>
  );
}

function PageHero({ eyebrow, title, subtitle, image, imageAlt, primary, secondary, openLead }) {
  return (
    <div className="bg-gradient-to-b from-[#eef3ff] to-white border-b border-slate-100">
      <div className={`container-cz py-14 sm:py-16 ${image ? "grid lg:grid-cols-2 gap-10 items-center" : "max-w-3xl"}`}>
        <div className={image ? "" : ""}>
          <p className="eyebrow mb-3">{eyebrow}</p>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-slate-900 leading-tight">{title}</h1>
          {subtitle && <p className="text-lg text-slate-600 mt-5">{subtitle}</p>}
          <div className="flex flex-wrap gap-3 mt-7">
            <Button onClick={() => openLead(primary?.type || "tour")} className="rounded-full h-12 px-7 gap-2">{primary?.label || "Book a Tour"} <ArrowRight className="w-4 h-4" /></Button>
            {secondary && <Button onClick={() => openLead(secondary.type)} variant="outline" className="rounded-full h-12 px-7 border-slate-300">{secondary.label}</Button>}
          </div>
        </div>
        {image && (
          <div className="rounded-3xl overflow-hidden shadow-cz aspect-[4/3]">
            <img src={image} alt={imageAlt || title} className="w-full h-full object-cover" />
          </div>
        )}
      </div>
    </div>
  );
}

export function AboutPage() {
  const c = usePageContent("/about-us");
  return (
    <Shell path="/about-us">
      {() => !c ? <Spinner /> : (
        <div className={`container-cz py-16 ${c.image ? "grid lg:grid-cols-2 gap-12 items-center max-w-6xl" : "max-w-3xl"}`}>
          <div>
            <p className="eyebrow mb-3">{c.eyebrow}</p>
            <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-slate-900">{c.title}</h1>
            {(c.paragraphs || []).map((p, i) => (
              <p key={i} className={`text-slate-600 ${i === 0 ? "text-lg mt-6" : "mt-4"}`}>{p}</p>
            ))}
          </div>
          {c.image && (
            <div className="rounded-3xl overflow-hidden shadow-cz aspect-[4/3]">
              <img src={c.image} alt={c.image_alt || c.title} className="w-full h-full object-cover" />
            </div>
          )}
        </div>
      )}
    </Shell>
  );
}

export function AmenitiesPage() {
  const { openLead } = useLead();
  const c = usePageContent("/amenities");
  return (
    <Shell path="/amenities">
      {() => !c ? <Spinner /> : (
        <>
          <PageHero eyebrow={c.eyebrow} title={c.title} subtitle={c.subtitle} image={c.image} imageAlt={c.image_alt}
            primary={{ label: "Book a Tour", type: "tour" }} secondary={{ label: "Get a Quote", type: "quote" }} openLead={openLead} />
          <div className="container-cz py-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="amenities-grid">
            {(c.items || []).map((a, i) => (
              <div key={i} className="card-cz p-6">
                <span className="w-11 h-11 rounded-xl bg-sky-50 text-primary flex items-center justify-center mb-4"><Icon name={a.icon} className="w-5 h-5" /></span>
                <h3 className="font-display text-lg font-bold text-slate-900">{a.title}</h3>
                <p className="text-sm text-slate-500 mt-2 leading-relaxed">{a.desc}</p>
              </div>
            ))}
          </div>
        </>
      )}
    </Shell>
  );
}

export function PricingPage() {
  const { openLead } = useLead();
  const c = usePageContent("/pricing");
  return (
    <Shell path="/pricing">
      {() => !c ? <Spinner /> : (
        <>
          <PageHero eyebrow={c.eyebrow} title={c.title} subtitle={c.subtitle} image={c.image} imageAlt={c.image_alt}
            primary={{ label: "Get a Quote", type: "quote" }} secondary={{ label: "Book a Tour", type: "tour" }} openLead={openLead} />
          <div className="container-cz py-14 grid md:grid-cols-3 gap-6" data-testid="pricing-grid">
            {(c.plans || []).map((p) => (
              <div key={p.name} className={`card-cz p-7 flex flex-col ${p.featured ? "ring-2 ring-primary shadow-cz" : ""}`}>
                {p.featured && <span className="self-start mb-3 text-xs font-semibold px-2.5 py-1 rounded-full bg-primary text-white">Most Popular</span>}
                <h3 className="font-display text-xl font-bold text-slate-900">{p.name}</h3>
                <p className="mt-3"><span className="font-display text-4xl font-extrabold text-slate-900">{p.price}</span><span className="text-slate-500">{p.unit}</span></p>
                <ul className="mt-5 space-y-2.5 flex-1">
                  {(p.features || []).map((f) => <li key={f} className="flex items-start gap-2 text-sm text-slate-600"><CheckCircle2 className="w-4 h-4 text-primary mt-0.5 shrink-0" /> {f}</li>)}
                </ul>
                {p.href ? (
                  <a href={p.href} target="_blank" rel="noopener noreferrer" data-testid={`plan-cta-${p.name.toLowerCase().replace(/ /g, "-")}`}
                    className="inline-flex items-center justify-center rounded-full h-11 mt-6 w-full bg-primary text-white text-sm font-medium hover:bg-primary/90 transition-colors">Get a Quote</a>
                ) : (
                  <Button onClick={() => openLead("quote")} data-testid={`plan-cta-${p.name.toLowerCase().replace(/ /g, "-")}`} className="rounded-full h-11 mt-6 w-full">Get a Quote</Button>
                )}
              </div>
            ))}
          </div>
          {c.footnote && <p className="container-cz pb-14 text-center text-sm text-slate-500">{c.footnote} <Link to="/contact" className="text-primary font-semibold">Talk to our team</Link> for a tailored quote.</p>}
        </>
      )}
    </Shell>
  );
}

export function ContactPage() {
  const c = usePageContent("/contact");
  return (
    <Shell path="/contact">
      {() => !c ? <Spinner /> : <ContactSections data={withContactDefaults(c)} />}
    </Shell>
  );
}
